import { z } from "zod";

/**
 * Copying a clinic's setup between environments (staging → production): a JSON file of named
 * records (no internal IDs; links are by name), imported section by section with a preview.
 * Never clients, visits, charts, payments, staff, iPads, or phone numbers.
 */
export const SETUP_SECTIONS = ["settings", "roles", "locations", "resources", "tags", "formulary", "suppliers", "catalog", "forms", "messages", "purchases", "inventory"] as const;
export type SetupSection = (typeof SETUP_SECTIONS)[number];

export const SETUP_SECTION_LABELS: Record<SetupSection, { label: string; description: string }> = {
  settings: { label: "Clinic settings", description: "Booking, deposits, messages on or off, mobile, reminders, charts, branding. No passwords or keys." },
  roles: { label: "Roles and permissions", description: "Each role and what it can do." },
  locations: { label: "Locations and hours", description: "Address, hours, and closures." },
  resources: { label: "Equipment", description: "Equipment types and their units." },
  tags: { label: "Tags", description: "Client tags, including which count as a good faith exam." },
  formulary: { label: "Formulary", description: "Medications, fluids, and supplies, with strengths and low-stock levels." },
  suppliers: { label: "Suppliers", description: "Who you buy from." },
  catalog: { label: "Catalog", description: "Categories, services, add-ons, products, memberships, and packages: prices, timing, recipes, who can do them, and which add-ons go on which services." },
  forms: { label: "Forms", description: "Intake, consent, and other forms (the current version of each)." },
  messages: { label: "Message templates", description: "Reminder and confirmation texts and emails." },
  purchases: { label: "Purchases", description: "Orders received, with their lines (not attached files)." },
  inventory: { label: "Inventory", description: "Lots on the shelf: lot number, BUD, and what's left." },
};

/** One named thing in a section (a service, a role, a lot). `data` has no internal IDs. */
export interface SetupRecord {
  key: string;
  label: string;
  data: Record<string, unknown>;
}

export interface SetupFile {
  kind: "vaulted-setup";
  version: 1;
  exportedAt: string;
  clinicName: string;
  sections: Partial<Record<SetupSection, SetupRecord[]>>;
}

const recordSchema = z.object({ key: z.string().min(1).max(500), label: z.string().max(500), data: z.record(z.string(), z.unknown()) });
export const setupFileSchema = z.object({
  kind: z.literal("vaulted-setup", { message: "That isn't a Vaulted setup file." }),
  version: z.literal(1),
  exportedAt: z.string(),
  clinicName: z.string(),
  sections: z.partialRecord(z.enum(SETUP_SECTIONS), z.array(recordSchema).max(20_000)),
});

export const setupImportSchema = z.object({
  file: setupFileSchema,
  sections: z.array(z.enum(SETUP_SECTIONS)).min(1, "Pick at least one section"),
  /** Items here already that should take the file's version, as "section:key". Others are left as they are. */
  overwrite: z.array(z.string().max(600)).max(20_000).default([]),
  /** Archived catalog items and formulary items are left out unless this is ticked. */
  includeArchived: z.boolean().default(false),
});
export type SetupImportInput = z.infer<typeof setupImportSchema>;

export interface SetupChange {
  field: string;
  here: string;
  file: string;
}

export interface SetupPreview {
  sections: {
    section: SetupSection;
    items: { key: string; label: string; status: "new" | "changed" | "same"; changes: SetupChange[] }[];
    /** Archived in the file, and left out (no "include archived"). */
    archivedLeftOut: number;
  }[];
}

export interface SetupImportResult {
  created: number;
  updated: number;
  unchanged: number;
  kept: number;
  /** Links that couldn't be made (a role or medication that isn't here), and what was left off. */
  notes: string[];
}

/** Archived in the file: a catalog item marked archived, or a formulary item that's off. */
export const archivedInSetup = (section: SetupSection, r: SetupRecord) =>
  (section === "catalog" && r.data.status === "archived") || (section === "formulary" && r.data.active === false);
